import React from 'react';
import { useMultiReports } from '../MultiReportsContext';

// Calculate metrics for Other Fields tab
function useOtherFieldsMetrics() {
  const { bookings, dateRange, activeFieldTab, dateFieldForTab } = useMultiReports();

  return React.useMemo(() => {
    if (activeFieldTab !== 'other_status') return null;
    
    let dateFiltered = [...bookings];
    if (dateRange.from) {
      dateFiltered = dateFiltered.filter(b => {
        const dateValue = b[dateFieldForTab];
        return dateValue && new Date(dateValue) >= new Date(dateRange.from);
      });
    }
    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59);
      dateFiltered = dateFiltered.filter(b => {
        const dateValue = b[dateFieldForTab];
        return dateValue && new Date(dateValue) <= toDate;
      });
    }
    
    return {
      inwards_status: {
        'Inwards Pending': dateFiltered.filter(b => b.inwards_status === 'Inwards Pending').length,
        'Inwards Done': dateFiltered.filter(b => b.inwards_status === 'Inwards Done').length,
        'Delivered': dateFiltered.filter(b => b.inwards_status === 'Delivered').length,
        'Blank': dateFiltered.filter(b => !b.inwards_status || b.inwards_status === '').length,
      },
      address_proof_received: {
        'Pending': dateFiltered.filter(b => b.address_proof_received === 'Pending').length,
        'Received': dateFiltered.filter(b => b.address_proof_received === 'Received').length,
        'Blank': dateFiltered.filter(b => !b.address_proof_received || b.address_proof_received === '').length,
      },
      photos: {
        'Pending': dateFiltered.filter(b => b.photos === 'Pending').length,
        'Received': dateFiltered.filter(b => b.photos === 'Received').length,
        'Blank': dateFiltered.filter(b => !b.photos || b.photos === '').length,
      },
      loan_status: {
        'pending': dateFiltered.filter(b => b.loan_status === 'pending').length,
        'approved': dateFiltered.filter(b => b.loan_status === 'approved').length,
        'disbursed': dateFiltered.filter(b => b.loan_status === 'disbursed').length,
        'rejected': dateFiltered.filter(b => b.loan_status === 'rejected').length,
        'Blank': dateFiltered.filter(b => !b.loan_status || b.loan_status === '').length,
      },
      total: dateFiltered.length
    };
  }, [bookings, dateRange, activeFieldTab, dateFieldForTab]);
}

// Calculate metrics for PME tab
function usePmeMetrics() {
  const { bookings, dateRange, activeFieldTab, dateFieldForTab } = useMultiReports();

  return React.useMemo(() => {
    if (activeFieldTab !== 'pme') return null;
    
    let dateFiltered = [...bookings];
    if (dateRange.from) {
      dateFiltered = dateFiltered.filter(b => {
        const dateValue = b[dateFieldForTab];
        return dateValue && new Date(dateValue) >= new Date(dateRange.from);
      });
    }
    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59);
      dateFiltered = dateFiltered.filter(b => {
        const dateValue = b[dateFieldForTab];
        return dateValue && new Date(dateValue) <= toDate;
      });
    }
    
    return {
      pme_submission_date: {
        'Has Date': dateFiltered.filter(b => b.pme_submission_date && b.pme_submission_date !== '').length,
        'Blank': dateFiltered.filter(b => !b.pme_submission_date || b.pme_submission_date === '').length,
      },
      pme_status: {
        'pending': dateFiltered.filter(b => b.pme_status === 'pending').length,
        'submitted': dateFiltered.filter(b => b.pme_status === 'submitted').length,
        'approved': dateFiltered.filter(b => b.pme_status === 'approved').length,
        'rejected': dateFiltered.filter(b => b.pme_status === 'rejected').length,
        'Blank': dateFiltered.filter(b => !b.pme_status || b.pme_status === '').length,
      },
      total: dateFiltered.length
    };
  }, [bookings, dateRange, activeFieldTab, dateFieldForTab]);
}

export function OtherFieldsMetrics() {
  const metrics = useOtherFieldsMetrics();
  const { otherFieldsFilters, setOtherFieldsFilters } = useMultiReports();

  if (!metrics) return null;

  return (
    <div className="mt-4 pt-4 border-t">
      <p className="text-xs font-medium text-gray-600 mb-3">Status Metrics & Filters</p>
      
      {/* Metrics Boxes — premium gradient KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 anim-grid-stagger">
        {/* Exchange Vehicle Inward Status */}
        <div className="rounded-2xl p-4 text-white shadow-[0_8px_24px_-6px_rgba(79,70,229,0.30)] hover:-translate-y-0.5 transition-all duration-240 ease-out relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 60%, #818CF8 100%)' }}>
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-90 relative z-10">Exchange Vehicle Inward</p>
          <div className="space-y-1 text-xs relative z-10">
            <div className="flex justify-between"><span className="opacity-85">Inwards Pending:</span><span className="font-bold">{metrics.inwards_status['Inwards Pending']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Inwards Done:</span><span className="font-bold">{metrics.inwards_status['Inwards Done']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Delivered:</span><span className="font-bold">{metrics.inwards_status['Delivered']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Blank:</span><span className="font-bold">{metrics.inwards_status['Blank']}</span></div>
          </div>
        </div>
        
        {/* Address Proof Status */}
        <div className="rounded-2xl p-4 text-white shadow-[0_8px_24px_-6px_rgba(124,58,237,0.30)] hover:-translate-y-0.5 transition-all duration-240 ease-out relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #9333EA 60%, #C026D3 100%)' }}>
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-90 relative z-10">Address Proof Status</p>
          <div className="space-y-1 text-xs relative z-10">
            <div className="flex justify-between"><span className="opacity-85">Pending:</span><span className="font-bold">{metrics.address_proof_received['Pending']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Received:</span><span className="font-bold">{metrics.address_proof_received['Received']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Blank:</span><span className="font-bold">{metrics.address_proof_received['Blank']}</span></div>
          </div>
        </div>
        
        {/* Photo Status */}
        <div className="rounded-2xl p-4 text-white shadow-[0_8px_24px_-6px_rgba(217,70,239,0.30)] hover:-translate-y-0.5 transition-all duration-240 ease-out relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #DB2777 0%, #E11D48 60%, #F59E0B 100%)' }}>
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-90 relative z-10">Photo Status</p>
          <div className="space-y-1 text-xs relative z-10">
            <div className="flex justify-between"><span className="opacity-85">Pending:</span><span className="font-bold">{metrics.photos['Pending']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Received:</span><span className="font-bold">{metrics.photos['Received']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Blank:</span><span className="font-bold">{metrics.photos['Blank']}</span></div>
          </div>
        </div>
        
        {/* Loan Status */}
        <div className="rounded-2xl p-4 text-white shadow-[0_8px_24px_-6px_rgba(16,185,129,0.30)] hover:-translate-y-0.5 transition-all duration-240 ease-out relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 60%, #0891B2 100%)' }}>
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-90 relative z-10">Loan Status</p>
          <div className="space-y-1 text-xs relative z-10">
            <div className="flex justify-between"><span className="opacity-85">Pending:</span><span className="font-bold">{metrics.loan_status['pending']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Approved:</span><span className="font-bold">{metrics.loan_status['approved']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Disbursed:</span><span className="font-bold">{metrics.loan_status['disbursed']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Rejected:</span><span className="font-bold">{metrics.loan_status['rejected']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Blank:</span><span className="font-bold">{metrics.loan_status['Blank']}</span></div>
          </div>
        </div>
      </div>
      
      {/* Dropdown Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Exchange Vehicle Inward</label>
          <select
            value={otherFieldsFilters.inwards_status}
            onChange={(e) => setOtherFieldsFilters(prev => ({ ...prev, inwards_status: e.target.value }))}
            className="w-full p-2 border rounded text-xs"
            data-testid="filter-inwards_status"
          >
            <option value="all">All</option>
            <option value="blank">Blank</option>
            <option value="Inwards Pending">Inwards Pending</option>
            <option value="Inwards Done">Inwards Done</option>
            <option value="Delivered">Delivered</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Address Proof Status</label>
          <select
            value={otherFieldsFilters.address_proof_received}
            onChange={(e) => setOtherFieldsFilters(prev => ({ ...prev, address_proof_received: e.target.value }))}
            className="w-full p-2 border rounded text-xs"
            data-testid="filter-address_proof"
          >
            <option value="all">All</option>
            <option value="blank">Blank</option>
            <option value="Pending">Pending</option>
            <option value="Received">Received</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Photo Status</label>
          <select
            value={otherFieldsFilters.photos}
            onChange={(e) => setOtherFieldsFilters(prev => ({ ...prev, photos: e.target.value }))}
            className="w-full p-2 border rounded text-xs"
            data-testid="filter-photos"
          >
            <option value="all">All</option>
            <option value="blank">Blank</option>
            <option value="Pending">Pending</option>
            <option value="Received">Received</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Loan Status</label>
          <select
            value={otherFieldsFilters.loan_status}
            onChange={(e) => setOtherFieldsFilters(prev => ({ ...prev, loan_status: e.target.value }))}
            className="w-full p-2 border rounded text-xs"
            data-testid="filter-loan_status"
          >
            <option value="all">All</option>
            <option value="blank">Blank</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="disbursed">Disbursed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
    </div>
  );
}

export function PmeMetrics() {
  const metrics = usePmeMetrics();
  const { pmeFilters, setPmeFilters } = useMultiReports();

  if (!metrics) return null;

  return (
    <div className="mt-4 pt-4 border-t">
      <p className="text-xs font-medium text-gray-600 mb-3">PME Status Metrics & Filters</p>
      
      {/* Metrics Boxes — premium gradient PME tiles */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-4 anim-grid-stagger">
        {/* PME Submission Date */}
        <div className="rounded-2xl p-4 text-white shadow-[0_8px_24px_-6px_rgba(67,56,202,0.30)] hover:-translate-y-0.5 transition-all duration-240 ease-out relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #6366F1 60%, #8B5CF6 100%)' }}>
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-90 relative z-10">PME Submission Date</p>
          <div className="space-y-1 text-xs relative z-10">
            <div className="flex justify-between"><span className="opacity-85">Has Date:</span><span className="font-bold">{metrics.pme_submission_date['Has Date']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Blank:</span><span className="font-bold">{metrics.pme_submission_date['Blank']}</span></div>
          </div>
        </div>
        
        {/* PME Status */}
        <div className="rounded-2xl p-4 text-white shadow-[0_8px_24px_-6px_rgba(20,184,166,0.30)] hover:-translate-y-0.5 transition-all duration-240 ease-out relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0891B2 0%, #14B8A6 60%, #10B981 100%)' }}>
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-90 relative z-10">PME Status</p>
          <div className="space-y-1 text-xs relative z-10">
            <div className="flex justify-between"><span className="opacity-85">Pending:</span><span className="font-bold">{metrics.pme_status['pending']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Submitted:</span><span className="font-bold">{metrics.pme_status['submitted']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Approved:</span><span className="font-bold">{metrics.pme_status['approved']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Rejected:</span><span className="font-bold">{metrics.pme_status['rejected']}</span></div>
            <div className="flex justify-between"><span className="opacity-85">Blank:</span><span className="font-bold">{metrics.pme_status['Blank']}</span></div>
          </div>
        </div>
      </div>
      
      {/* Dropdown Filters */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">PME Submission Date</label>
          <select
            value={pmeFilters.pme_submission_date}
            onChange={(e) => setPmeFilters(prev => ({ ...prev, pme_submission_date: e.target.value }))}
            className="w-full p-2 border rounded text-xs"
            data-testid="filter-pme_submission_date"
          >
            <option value="all">All</option>
            <option value="blank">Blank</option>
            <option value="filled">Has Date</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">PME Status</label>
          <select
            value={pmeFilters.pme_status}
            onChange={(e) => setPmeFilters(prev => ({ ...prev, pme_status: e.target.value }))}
            className="w-full p-2 border rounded text-xs"
            data-testid="filter-pme_status"
          >
            <option value="all">All</option>
            <option value="blank">Blank</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
    </div>
  );
}
